import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { IoService } from '../../../services/io/io.service';
import { LocationService } from '../../../services/location/location.service';
import { ClientSettingsService } from '../../../services/client-settings/client-settings.service';

// Full-screen, read-only Product Overview (routed child of the retail-product
// detail, launched by the View's 'overview' route action; the app header stays
// visible). Shows the product's active View fields as read-only info, plus a
// per-location grid of on-hand quantity and units sold over trailing
// 7/14/30/90/365-day windows with company-wide totals. More panels planned.
@Component({
  selector: 'app-product-overview',
  templateUrl: './product-overview.component.html',
  styleUrls: ['./product-overview.component.scss']
})
export class ProductOverviewComponent implements OnInit, OnDestroy {

  viewKey: string;
  documentId: string;

  productName: string = null;
  // Stamped when the data loads; shown in the per-page print footer.
  reportDate: Date = new Date();
  // {name, value} pairs derived from the view's active fields.
  infoFields: { name: string, value: string }[] = [];
  overview: any = null;
  loading = false;
  loadError: string = null;
  // The Replenish Target column only shows once the Stock Targets replenish
  // strategy is enabled for the client — until then the values are unused.
  showReplenishTarget = false;

  private paramsSub: Subscription;
  private clientSettingsSub: Subscription;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private ioService: IoService,
    private locationService: LocationService,
    private clientSettingsService: ClientSettingsService,
  ) { }

  ngOnInit(): void {
    // Flags the body so the global print stylesheet can strip the app chrome
    // and let this screen flow across pages (see styles.scss @media print).
    window.document.body.classList.add('product-overview-open');

    this.showReplenishTarget = this.clientSettingsService.isReplenishStockTargetsEnabled();
    this.clientSettingsSub = this.clientSettingsService.clientSettingsLoadedObservable().subscribe(() => {
      this.showReplenishTarget = this.clientSettingsService.isReplenishStockTargetsEnabled();
    });

    this.paramsSub = this.route.paramMap.subscribe(params => {
      this.viewKey = params.get('viewKey');
      this.documentId = params.get('documentId');
      if (this.viewKey && this.documentId) {
        this.load();
      }
    });
  }

  ngOnDestroy(): void {
    window.document.body.classList.remove('product-overview-open');
    if (this.paramsSub) this.paramsSub.unsubscribe();
    if (this.clientSettingsSub) this.clientSettingsSub.unsubscribe();
  }

  // Non-countable items (lottery, gift cards…) post balances by design but are
  // not stock: the server reports their on hand as null and this flag blanks
  // the on-hand, days-supply and sell-through cells.
  get notCountable(): boolean {
    return !!this.overview && this.overview.inventoryCountable === false;
  }

  async load() {
    this.loading = true;
    this.loadError = null;
    try {
      const [viewResult, overview] = await Promise.all([
        this.ioService.post('/view/getView', {
          viewKey: this.viewKey,
          documentId: this.documentId,
          locationId: this.locationService.getActiveLocation()?._id || null,
          // Without returnContext the endpoint returns just {document} — the
          // full {view, displayValues, userActiveFields, ...} shape needs it.
          returnContext: true,
          ignoreAlias: true,
        }) as any,
        this.ioService.post('/account/getProductOverview', {
          accountId: this.documentId,
        }) as any,
      ]);
      this.overview = overview;
      this.productName = overview.name;
      this.reportDate = new Date();
      this.buildInfoFields(viewResult);
    } catch (e) {
      console.log('product overview load error: ', e);
      this.loadError = e?.error?.msg || 'Failed to load product overview.';
    }
    this.loading = false;
  }

  // Read-only {label, value} pairs from the view's effective active fields —
  // the same resolution getView applies (user override, else view.activeFields,
  // else all fields). Relational/media field types are skipped.
  private buildInfoFields(viewResult: any) {
    const view = viewResult.view;
    const document = viewResult.document || {};
    const displayValues = viewResult.displayValues || {};

    const activePaths: string[] = (viewResult.userActiveFields?.length ? viewResult.userActiveFields : null)
      || (view.activeFields?.length ? view.activeFields : null)
      || view.fields.map(f => f.path);

    const skipTypes = ['image', 'childList', 'parentLink', 'moveItems'];
    this.infoFields = [];
    for (const path of activePaths) {
      const field = view.fields.find(f => f.path === path);
      if (!field || skipTypes.indexOf(field.type) !== -1) continue;
      const value = this.formatValue(field, document[field.path], displayValues[field.path]);
      this.infoFields.push({ name: field.name, value });
    }
  }

  private formatValue(field: any, value: any, displayValue: any): string {
    if (field.type === 'sourceCollection') {
      if (Array.isArray(displayValue)) {
        return displayValue.map(entry => entry?.display ?? entry).join(', ') || '—';
      }
      return displayValue || '—';
    }
    if (value == null || value === '') return '—';
    if (field.type === 'currency' || field.type === 'advancedPrice') {
      return '$' + (value / 100).toFixed(2);
    }
    if (field.type === 'number' && field.format === 'percentage') {
      return parseFloat((value * 100).toFixed(4)) + '%';
    }
    if (field.type === 'calculated' || field.calculated === true) {
      if (field.format === 'currency' || field.type === 'currency') return '$' + (value / 100).toFixed(2);
      return String(value);
    }
    if (field.type === 'boolean') {
      return value ? 'Yes' : 'No';
    }
    if (field.type === 'dateTime') {
      return new Date(value).toLocaleString();
    }
    if (field.type === 'richText') {
      // Strip markup for the read-only summary line.
      const el = window.document.createElement('div');
      el.innerHTML = value;
      return el.textContent.trim() || '—';
    }
    if (Array.isArray(value)) {
      return value.join(', ') || '—';
    }
    return String(value);
  }

  // ── Velocity helpers (per row or the totals object) ──────────────

  // On hand ÷ average daily sales over the past 30 days. '—' with no sales.
  daysSupply(row: any): string {
    if (!row || !row.d30 || row.d30 <= 0) return '—';
    const days = row.onHand / (row.d30 / 30);
    if (days <= 0) return '0';
    return days >= 100 ? Math.round(days).toString() : (Math.round(days * 10) / 10).toString();
  }

  daysSupplyLow(row: any): boolean {
    if (!row || !row.d30 || row.d30 <= 0) return false;
    return (row.onHand / (row.d30 / 30)) < 7;
  }

  // Units sold in 30 days ÷ (sold + on hand).
  sellThrough(row: any): string {
    if (!row) return '—';
    const denominator = (row.d30 || 0) + Math.max(0, row.onHand || 0);
    if (denominator <= 0 || !row.d30) return '—';
    return Math.round((row.d30 / denominator) * 100) + '%';
  }

  shortDate(value: any): string {
    if (!value) return '—';
    return new Date(value).toLocaleDateString();
  }

  formatCents(value: any): string {
    if (value == null || value === '') return '—';
    const cents = Number(value);
    if (!isFinite(cents)) return String(value);
    return '$' + (cents / 100).toFixed(2);
  }

  close() {
    this.router.navigate(['details', this.viewKey, this.documentId], {
      relativeTo: this.route.parent,
      queryParamsHandling: 'preserve',
    });
  }
}
